import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Skeleton, Table } from 'antd';
import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';
import moment from 'moment';
import { debounce, get } from 'lodash';
import { ColumnProps } from 'antd/es/table';
import IconFont from '@/components/IconFont';
import EditCellTable, { IcustomColumnType } from '@/components/EditCellTable';
import { getMoneyRulesItem } from '@/components/InputMoney/validator';
import SelectDiscountCoupon, { IselectDiscountCouponRef } from '@/components/SelectDiscountCoupon';
import BatchModifyPriceModal, {
  BatchModifyPriceModalRef,
} from '@/components/Reservation/BatchModifyPriceModal';
import BatchCouponModal, { IRefProps } from '@/components/BatchCouponModal';
import { batchCalcCouponValue } from '@/redux/actions/reservation/helper';
import { saveData, updateOrderRoomPrice } from '@/redux/actions/order';
import { getManualCouponList, getWangyuCouponList } from '@/redux/actions/guest';
import { fetchCouponCalculate, fetchUserCouponCount } from '@/api/guest';
import { renderIntlLabel, uuid } from '@/utils';
import AuthBtn from '@/components/AuthBtn';
import { HistoryParams } from '@/utils/type';
import {
  CouponStatus,
  CouponTabStatus,
  DATE_FORMAT,
  OccType,
  PERM_CODES,
  PriceChannel,
  SubOrderStatus,
} from '@/config';
import BigNumber, {
  isNil,
  parseFen2Yuan,
  parseYuan2Fen,
  parse2Number,
  parseYuan2Currency,
} from '@/utils/number';
import { getTimeByUtcOffset } from '@/utils/intl';
import style from './index.module.scss';

const getRoomColumns = (isHasColumns, dayPriceDataSource): Array<IcustomColumnType<any>> => {
  return [
    {
      title: renderIntlLabel('order_roomorder_room_type'),
      dataIndex: 'roomTypeName',
      // 如果存在动态日期则 fixed: left
      fixed: isHasColumns ? 'left' : false,
      width: 160,
    },
    {
      title: renderIntlLabel('order_roomorder_room_number'),
      dataIndex: 'roomNo',
      render: (val, record) =>
        (record.status === SubOrderStatus.Reservation ||
          record.status === SubOrderStatus.CancelReservation) &&
        !val
          ? renderIntlLabel('order_wait_arrange')
          : val,
      fixed: isHasColumns ? 'left' : false,
      width: 130,
    },
    {
      title: renderIntlLabel('order_deposit'),
      dataIndex: 'deposit',
      shouldEditCell: (record) => {
        let editable = false;
        let color = '#969799';
        // 钟点房如果在日租房中出现则不可编辑
        if (
          record.status === SubOrderStatus.CheckIn &&
          !dayPriceDataSource?.map((item) => item.roomId)?.includes(record.roomId)
        ) {
          editable = true;
          color = '#323233';
        }
        return {
          editable,
          rules: [
            { required: true, message: renderIntlLabel('order_please_input_deposit') },
            getMoneyRulesItem(),
          ],
          cellInputType: 'number',
          cellPrefix: '',
          cellStyle: {
            color,
          },
        };
      },
      fixed: isHasColumns ? 'left' : false,
      width: 130,
      align: 'right',
    },
  ];
};

const renderTableTitle = (dataIndex) => {
  return get(dataIndex?.split('.'), '0')?.replace(/^\[(.*)\]$/, '$1');
};

const RoomPrice = ({
  orderBaseInfo,
  wangyuCouponList,
  manualCouponList,
  dayRangeDateList,
  dayPriceDataSource,
  hourRangeDateList,
  hourPriceDataSource,
  orderRoomInfoLoading,
  saveDataFn,
  updateOrderRoomPriceFn,
  getWangyuCouponListFn,
  getManualCouponListFn,
}) => {
  const { resvNo } = useParams<HistoryParams>();
  const [isModify, setIsModify] = useState<boolean>(false);

  const [isShowChooseBtn, setIsShowChooseBtn] = useState<boolean>(false);

  const priceRef = useRef<any>();
  const selectCouponRef = useRef<IselectDiscountCouponRef>();
  const batchChooseCouponRef = useRef<IRefProps>();
  const batchModalRef = useRef<BatchModifyPriceModalRef>();

  const getAllPriceMap = (occType, dataSource, rangeDateList) => {
    const obj = {};
    dataSource?.forEach((item) => {
      const { recNo, deposit } = item;
      obj[occType + recNo] = Number(deposit || 0);
      // dateIdKey 日期和index为key,兼容钟点房同一天续住
      rangeDateList.forEach((dateIdKey) => {
        const recNoDateIdKey = recNo + dateIdKey;
        // 房价
        obj[occType + recNoDateIdKey] = Number(item[dateIdKey] || 0);
        // 优惠券
        const { couponNo, couponAmount } = item[recNoDateIdKey] || {};
        if (couponNo) {
          const couponKey = recNoDateIdKey + couponNo;
          obj[occType + couponKey] = isNil(couponAmount) ? undefined : Number(couponAmount);
        }
      });
    });
    return obj;
  };

  // 比较修改的内容是否和原来一样
  const compareIsEqual = (targetMap) => {
    const sourceMap = priceRef.current;
    // console.log('compareIsEqual', targetMap, sourceMap);
    const isDifferent =
      Object.keys(targetMap).some((key) => targetMap[key] !== sourceMap[key]) ||
      Object.keys(sourceMap).some((key) => sourceMap[key] !== targetMap[key]);
    setIsModify(isDifferent);
  };

  // 根据前端选择的券同步更新可用券列表状态
  const updateBatchWyCouponStatus = (statusMap) => {
    if (Array.isArray(wangyuCouponList)) {
      let allWyCouponList = [...wangyuCouponList];
      allWyCouponList = allWyCouponList?.map((item) => {
        if (!isNil(statusMap[item.couponNo])) {
          return { ...item, status: statusMap[item.couponNo] };
        }
        return item;
      });
      saveDataFn({ wangyuCouponList: allWyCouponList });
    }
  };

  const calcRoomPriceByCouponNo = async ({ couponNo, businessDate, occType, roomPrice }) => {
    if (couponNo) {
      try {
        const res = await fetchCouponCalculate({
          businessDate,
          wyuid: orderBaseInfo?.ownerWyuid,
          occType,
          roomPrice,
          lockCouponNo: couponNo,
        });
        if (res?.code === 0) {
          const { couponAmount } = res?.data || {};
          return couponAmount;
        }
        // return 0;
      } catch (error) {
        // return 0;
      }
    }
    return 0;
  };

  // 本地缓存房价
  const handleSaveDayCellChange = async (data, rowIndex, dataIndex, isSaveFailed) => {
    const resultList = [...data];
    resultList[rowIndex].isSaveFailed = isSaveFailed;

    // 计算优惠券金额
    const record = resultList[rowIndex];
    const couponItem = record?.[record?.recNo + dataIndex] || {};
    const number = await calcRoomPriceByCouponNo({
      couponNo: couponItem?.couponNo,
      businessDate: renderTableTitle(dataIndex),
      occType: OccType.DayUse,
      roomPrice: parse2Number(parseYuan2Fen(record?.[dataIndex] || 0)),
    });
    // 更新表格优惠券面额
    const couponObj = { ...couponItem };
    couponObj.couponAmount = Number(parseFen2Yuan(number));
    if (number === 0) {
      couponObj.couponNo = null;
      couponObj.couponName = null;
      couponObj.couponType = null;
      couponObj.couponSubType = null;
      couponObj.couponWyuid = null;
    }
    record[record?.recNo + dataIndex] = couponObj;
    resultList.splice(rowIndex, 1, record);
    // 更新优惠券可否点击
    updateBatchWyCouponStatus({
      [couponItem?.couponNo]: number ? CouponStatus.Locked : CouponStatus.Effective,
    });
    selectCouponRef.current.batchUpdateCacheCouponStatus({
      [couponItem?.couponNo]: number ? CouponStatus.Locked : CouponStatus.Effective,
    });

    saveDataFn({ dayPriceDataSource: [...resultList] });

    if (!isSaveFailed) {
      // 日租房押金修改后需同步修改钟点房的押金
      let tempHourList = [...hourPriceDataSource];
      tempHourList = tempHourList.map((item) => {
        const tempItem = { ...item };
        const index = resultList?.findIndex((dayItem) => dayItem.roomId === tempItem.roomId);
        if (index !== -1) {
          tempItem.deposit = resultList[index]?.deposit;
        }
        return tempItem;
      });
      saveDataFn({ hourPriceDataSource: tempHourList });
      const dayPriceMap = getAllPriceMap(OccType.DayUse, resultList, dayRangeDateList);
      const hourPriceMap = getAllPriceMap(OccType.TimeRENT, tempHourList, hourRangeDateList);
      compareIsEqual({ ...dayPriceMap, ...hourPriceMap });
    }
  };

  const handleSaveHourCellChange = async (data, rowIndex, dataIndex, isSaveFailed) => {
    const resultList = [...data];
    resultList[rowIndex].isSaveFailed = isSaveFailed;

    // 计算优惠券金额
    const record = resultList[rowIndex];
    const couponItem = record?.[record?.recNo + dataIndex] || {};
    const number = await calcRoomPriceByCouponNo({
      couponNo: couponItem?.couponNo,
      businessDate: renderTableTitle(dataIndex),
      occType: OccType.TimeRENT,
      roomPrice: parse2Number(parseYuan2Fen(record?.[dataIndex] || 0)),
    });
    // 更新表格优惠券面额
    const couponObj = { ...couponItem };
    couponObj.couponAmount = number;
    if (number === 0) {
      couponObj.couponNo = null;
      couponObj.couponName = null;
      couponObj.couponType = null;
      couponObj.couponSubType = null;
      couponObj.couponWyuid = null;
    }
    record[record?.recNo + dataIndex] = couponObj;
    resultList.splice(rowIndex, 1, record);
    // 更新优惠券可否点击
    updateBatchWyCouponStatus({
      [couponItem?.couponNo]: number ? CouponStatus.Locked : CouponStatus.Effective,
    });
    selectCouponRef.current.batchUpdateCacheCouponStatus({
      [couponItem?.couponNo]: number ? CouponStatus.Locked : CouponStatus.Effective,
    });

    saveDataFn({ hourPriceDataSource: [...resultList] });
    if (!isSaveFailed) {
      const dayPriceMap = getAllPriceMap(OccType.DayUse, dayPriceDataSource, dayRangeDateList);
      const hourPriceMap = getAllPriceMap(OccType.TimeRENT, resultList, hourRangeDateList);
      compareIsEqual({ ...dayPriceMap, ...hourPriceMap });
    }
  };

  // 循环遍历入参
  const formatRequestParams = (dataSource, rangeDateList) => {
    const updateRoomPriceVOList = [];
    dataSource?.forEach((item) => {
      rangeDateList.forEach((date) => {
        const recNoDateIdKey = item.recNo + date;
        const price = item[date];

        const { id, couponNo, couponAmount, couponName, couponType, couponSubType, couponWyuid } =
          item[recNoDateIdKey] || {};
        if (id) {
          const obj = {
            id,
            price: isNil(price) || price === 0 ? undefined : parse2Number(parseYuan2Fen(price)),
            couponNo,
            couponAmount:
              isNil(couponAmount) || couponAmount === 0
                ? undefined
                : parse2Number(parseYuan2Fen(couponAmount)),
            couponName,
            couponType,
            couponSubType,
            couponWyuid,
          };
          updateRoomPriceVOList.push(obj);
        }
      });
    });
    return updateRoomPriceVOList;
  };

  const handleSaveAllPrice = debounce(() => {
    if (
      dayPriceDataSource?.some((item) => item.isSaveFailed) ||
      hourPriceDataSource?.some((item) => item.isSaveFailed)
    ) {
      // 输入校验是否失败
      return;
    }
    const udpateDepositVOList = dayPriceDataSource.concat(hourPriceDataSource)?.map((item) => ({
      recNo: item.recNo,
      deposit: isNil(item.deposit) ? undefined : parse2Number(parseYuan2Fen(item.deposit)),
    }));
    const dayPrice = formatRequestParams(dayPriceDataSource, dayRangeDateList);
    const hourPrice = formatRequestParams(hourPriceDataSource, hourRangeDateList);
    updateOrderRoomPriceFn({
      resvNo,
      udpateDepositVOList,
      updateRoomPriceVOList: [...dayPrice, ...hourPrice],
    }).then(() => {
      setIsModify(false);
    });
  }, 200);

  const onCouponClick = (
    dateIdKey,
    val,
    priceChannel,
    rentDate,
    record,
    isClickCoupon,
    ownerWyuid,
    isDayRoom,
  ) => {
    const recordObj = { ...record };
    const { recNo } = recordObj;
    const recNoDateIdKey = recNo + dateIdKey;
    const dateObj = recordObj[recNoDateIdKey] ? { ...recordObj[recNoDateIdKey] } : {};
    if (isClickCoupon && ownerWyuid) {
      selectCouponRef.current.openModal({
        channelType: priceChannel,
        businessDate: getTimeByUtcOffset(rentDate, DATE_FORMAT) as any,
        wyuid: ownerWyuid,
        amount: parse2Number(parseYuan2Fen(val || 0)),
        occType: isDayRoom ? OccType.DayUse : OccType.TimeRENT,
        id: dateObj.id,
        changedCouponId: dateObj.couponNo ? [dateObj.couponNo] : [],
        xuanzhu: { remark: '订单详情' },
        callback: (couponNoArray, couponRecordArray) => {
          const couponStatusMap = {};
          if (dateObj.couponNo) {
            // 释放原来的券
            couponStatusMap[dateObj.couponNo] = CouponStatus.Effective;
          }
          // 未选择时couponNo为空
          const couponNo = get(couponNoArray, '[0]', '');
          if (couponNo) {
            couponStatusMap[couponNo] = CouponStatus.Locked;
          }
          // 更新网鱼券状态
          updateBatchWyCouponStatus(couponStatusMap);
          selectCouponRef.current.batchUpdateCacheCouponStatus(couponStatusMap);

          // 更新手工券状态
          getManualCouponListFn({ wyuid: ownerWyuid });

          if (Object.keys(dateObj).length) {
            dateObj.couponAmount = isNil(get(couponRecordArray, '[0].couponFaceValue'))
              ? undefined
              : Number(parseFen2Yuan(get(couponRecordArray, '[0].couponFaceValue')));
            dateObj.couponNo = get(couponRecordArray, '[0].couponNo');
            dateObj.couponName = get(couponRecordArray, '[0].couponName');
            dateObj.couponType = get(couponRecordArray, '[0].couponType');
            dateObj.couponSubType = get(couponRecordArray, '[0].couponSubType');
            dateObj.couponWyuid = get(couponRecordArray, '[0].wyuid');
            recordObj[recNoDateIdKey] = dateObj;
            if (isDayRoom) {
              const dayIndex = dayPriceDataSource.findIndex((item) => recNo === item.recNo);
              if (dayIndex !== -1) {
                const array = [...dayPriceDataSource];
                array.splice(dayIndex, 1, recordObj);
                // 本地比较
                const dayPriceMap = getAllPriceMap(OccType.DayUse, array, dayRangeDateList);
                const hourPriceMap = getAllPriceMap(
                  OccType.TimeRENT,
                  hourPriceDataSource,
                  hourRangeDateList,
                );
                compareIsEqual({ ...dayPriceMap, ...hourPriceMap });

                saveDataFn({ dayPriceDataSource: array });
              }
            } else {
              const hourIndex = hourPriceDataSource.findIndex((item) => recNo === item.recNo);
              if (hourIndex !== -1) {
                const array = [...hourPriceDataSource];
                array.splice(hourIndex, 1, recordObj);

                const dayPriceMap = getAllPriceMap(
                  OccType.DayUse,
                  dayPriceDataSource,
                  dayRangeDateList,
                );
                const hourPriceMap = getAllPriceMap(OccType.TimeRENT, array, hourRangeDateList);
                compareIsEqual({ ...dayPriceMap, ...hourPriceMap });

                saveDataFn({ hourPriceDataSource: array });
              }
            }
          }
        },
      });
    }
  };

  const initBatchChooseBtn = async () => {
    // 是否显示批量选券
    try {
      const res = await fetchUserCouponCount({
        wyuid: orderBaseInfo.ownerWyuid,
      });
      if (res.code === 0) {
        setIsShowChooseBtn(!!res.data.currentShopAvailQty);
      } else {
        setIsShowChooseBtn(false);
      }
    } catch (error) {
      setIsShowChooseBtn(false);
    }
  };

  useEffect(() => {
    // 记录第一次进入页面初值
    if (!priceRef.current || !Object.keys(priceRef.current).length) {
      const dayPriceMap = getAllPriceMap(OccType.DayUse, dayPriceDataSource, dayRangeDateList);
      const hourPriceMap = getAllPriceMap(OccType.TimeRENT, hourPriceDataSource, hourRangeDateList);
      priceRef.current = { ...dayPriceMap, ...hourPriceMap };
      // console.log('init---->>priceMap', priceRef.current, dayPriceDataSource, hourPriceDataSource);
    }
  }, [dayPriceDataSource, dayRangeDateList, hourPriceDataSource, hourRangeDateList]);

  useEffect(() => {
    if (orderBaseInfo.ownerWyuid) {
      getWangyuCouponListFn({
        wyuid: orderBaseInfo.ownerWyuid,
        couponStatus: CouponTabStatus.CanUse,
      });
      getManualCouponListFn({ wyuid: orderBaseInfo.ownerWyuid });

      initBatchChooseBtn();
    }
  }, [orderBaseInfo.ownerWyuid]);

  const getRoomPriceIsDisable = (editFlag: 0 | 1, status: SubOrderStatus) => {
    // 预订取消或者已退房直接disabled
    if (status === SubOrderStatus.CancelReservation || status === SubOrderStatus.CheckOut) {
      return true;
    }
    // editFlag = 0 过夜审,不可编辑
    return editFlag === 0;
  };

  const getRoomPriceIsDisplay = (editFlag: 0 | 1, status: SubOrderStatus) => {
    if (editFlag === null || editFlag === undefined) {
      return false;
    }
    // 预订取消或者已退房，editFlag === 1未过夜审房费不展示
    if (
      (status === SubOrderStatus.CancelReservation || status === SubOrderStatus.CheckOut) &&
      editFlag === 1
    ) {
      return false;
    }
    return true;
  };

  const getCouponIsAble = (
    isExistUnlockWyCoupon,
    allLockWyCouponList,
    couponNo,
    isExistUnusedManualCoupon,
    priceChannel,
  ) => {
    if (
      // 存在手工券
      isExistUnusedManualCoupon ||
      (priceChannel === PriceChannel.WALK_IN &&
        // 线下存在非锁定券 或者 当前间夜券在锁定中
        (isExistUnlockWyCoupon ||
          allLockWyCouponList?.map((item) => item.couponNo)?.includes(couponNo)))
    ) {
      return true;
    }
    return false;
  };

  const getRangeDateColumns = (
    list,
    isDayRoom,
    baseInfo,
    wangyuList,
    manualList,
  ): ColumnProps<any> => {
    // 券可否点击
    const allAvalibleWyCouponList = wangyuList;
    // 是否存在未锁定网鱼券
    const isExistUnlockWyCoupon = !!allAvalibleWyCouponList?.filter(
      (item) => item.status !== CouponStatus.Locked,
    )?.length;
    // 所有锁定的网鱼券
    const allLockWyCouponList = allAvalibleWyCouponList?.filter(
      (item) => item.status === CouponStatus.Locked,
    );
    // 未使用（可用）手工券
    const isExistUnusedManualCoupon = !!manualList?.find((item) => item.status === 0)?.xzCouponList
      ?.length;

    return list?.map((dateIdKey) => ({
      title: renderTableTitle(dateIdKey),
      dataIndex: dateIdKey,
      shouldEditCell: (record) => {
        let editable = false;
        let color = '#969799';
        const recNoDateIdKey = record.recNo + dateIdKey;
        const { editFlag } = record[recNoDateIdKey] || {};
        if (!getRoomPriceIsDisable(editFlag, record.status)) {
          editable = true;
          color = '#323233';
        }
        const isDisplay = getRoomPriceIsDisplay(editFlag, record.status);
        return {
          editable,
          rules: [
            { required: true, message: renderIntlLabel('order_please_input_roomPrice') },
            getMoneyRulesItem(),
          ],
          cellDisplay: isDisplay,
          cellInputType: 'number',
          cellPrefix: '',
          cellStyle: {
            color,
          },
        };
      },
      render: (val, record) => {
        const recNoDateIdKey = record.recNo + dateIdKey;
        const { couponNo, couponAmount, couponWyuid, editFlag, priceChannel, rentDate } =
          record[recNoDateIdKey] || {};
        // 是否过夜审
        const isDisable = getRoomPriceIsDisable(editFlag, record.status);
        // 网鱼券（线下渠道）、手工券点击条件判断
        const isExistAbleCoupon = getCouponIsAble(
          isExistUnlockWyCoupon,
          allLockWyCouponList,
          couponNo,
          isExistUnusedManualCoupon,
          priceChannel,
        );
        const isClickCoupon = !isDisable && isExistAbleCoupon;

        return (
          <>
            {/* 券按钮 */}
            <div
              className={`coupon ${style.coupon} ${
                isClickCoupon && baseInfo.ownerWyuid ? '' : style.disabled
              }`}
              onClick={() =>
                onCouponClick(
                  dateIdKey,
                  val,
                  priceChannel,
                  rentDate,
                  record,
                  isClickCoupon,
                  baseInfo.ownerWyuid,
                  isDayRoom,
                )
              }
            >
              {couponWyuid && couponNo && couponAmount ? (
                <div className={style.couponAmount}>{parseYuan2Currency(-couponAmount)}</div>
              ) : (
                <IconFont className={style.couponBtn} type="icon-mendianzengquantongji" />
              )}
            </div>
          </>
        );
      },
      width: 130,
      align: 'right',
    }));
  };

  const [dayRoomColumns, hourRoomColumns] = useMemo(() => {
    const dayColumns = getRoomColumns(!!dayRangeDateList.length, []);
    const hourColumns = getRoomColumns(!!hourRangeDateList.length, dayPriceDataSource);
    const dayRangeColumns = getRangeDateColumns(
      dayRangeDateList,
      true,
      orderBaseInfo,
      wangyuCouponList,
      manualCouponList,
    );
    const hourRangeColumns = getRangeDateColumns(
      hourRangeDateList,
      false,
      orderBaseInfo,
      wangyuCouponList,
      manualCouponList,
    );
    return [dayColumns.concat(dayRangeColumns), hourColumns.concat(hourRangeColumns)];
  }, [
    dayRangeDateList,
    hourRangeDateList,
    dayPriceDataSource,
    hourPriceDataSource,
    orderBaseInfo,
    wangyuCouponList,
    manualCouponList,
  ]);

  // 计算每一列总数
  const tableSummary = (pageData, rangeDateList) => {
    let totalRoomCounts = 0;
    let totalDeposit = 0;
    pageData?.forEach(({ deposit }) => {
      totalRoomCounts += 1;
      totalDeposit += Number(Number(deposit || 0).toFixed(2));
    });
    const datePriceCountsArray = [];
    rangeDateList?.forEach((dateIdKey) => {
      let counts = 0;
      pageData.forEach((subItem) => {
        const { recNo, status } = subItem;
        let roomPrice = subItem[dateIdKey] || 0;
        const { editFlag, couponWyuid } = subItem[recNo + dateIdKey] || {};
        let { couponAmount = 0 } = subItem[recNo + dateIdKey] || {};
        // 不展示的房费不参与计算
        const isDisplay = getRoomPriceIsDisplay(editFlag, status);
        if (!isDisplay) {
          roomPrice = 0;
          couponAmount = 0;
        }
        // 房价
        counts += Number(Number(roomPrice).toFixed(2));
        // 优惠券---不是同一个人即使有金额也不显示不计算
        if (couponWyuid && couponAmount) {
          counts -= Number(Number(couponAmount).toFixed(2));
        }
        counts = counts <= 0 ? 0 : counts;
      });
      datePriceCountsArray.push(counts.toFixed(2));
    });
    datePriceCountsArray.unshift(totalDeposit.toFixed(2));
    datePriceCountsArray.unshift(totalRoomCounts);
    return datePriceCountsArray;
  };

  // 计算统计行总数
  const [totalDayPrice, totalHourPrice] = useMemo(() => {
    let totalDay = 0;
    let totalHour = 0;
    if (Array.isArray(dayRangeDateList) && Array.isArray(dayPriceDataSource)) {
      const tempAttr = tableSummary(dayPriceDataSource, dayRangeDateList);
      totalDay = tempAttr.reduce((total, value, index) => {
        let tempVal = total;
        // 不算押金
        if (index > 1) {
          tempVal += Number(Number(value || 0).toFixed(2));
        }
        return tempVal;
      }, 0);
    }
    if (Array.isArray(hourRangeDateList) && Array.isArray(hourPriceDataSource)) {
      const tempAttr = tableSummary(hourPriceDataSource, hourRangeDateList);
      totalHour = tempAttr.reduce((total, value, index) => {
        let tempVal = total;
        if (index > 1) {
          tempVal += Number(Number(value || 0).toFixed(2));
        }
        return tempVal;
      }, 0);
    }
    return [totalDay?.toFixed(2), totalHour?.toFixed(2)];
  }, [dayRangeDateList, dayPriceDataSource, hourRangeDateList, hourPriceDataSource]);

  // 展示批量填写房价
  const showBatchPriceModal = () => {
    if (dayPriceDataSource && dayPriceDataSource.length) {
      batchModalRef.current.openModal(
        dayPriceDataSource.map((item) => ({
          indexId: item.id,
          roomTypeName: item.roomTypeName,
          roomNo: item.roomNo,
        })),
        1,
      );
    }
  };

  // 批量填写回调
  const handleBatchUpdatePrice = async (
    list: { indexId: string; inputPrice: string; deposit: string }[],
  ) => {
    if (dayPriceDataSource?.length && dayRangeDateList?.length) {
      // 更新的房价
      const updatePriceValueMap = {};
      // 更新的押金
      const updateDepositValueMap = {};
      list.forEach((item) => {
        updatePriceValueMap[item.indexId] = item.inputPrice;
        updateDepositValueMap[item.indexId] = item.deposit;
      });
      const updateCouponInfoList = []; // 重新计算金额的券列表
      const updateCouponNoMap = {}; // 重新计算金额的券map
      dayPriceDataSource.forEach((roomItem) => {
        const { id, status } = roomItem;
        const updatePrice = updatePriceValueMap[id];
        // 没有修改房间房价 或者 已取消 或者 已退房订单
        if (
          !updatePrice ||
          status === SubOrderStatus.CancelReservation ||
          status === SubOrderStatus.CheckOut
        )
          return;
        dayRangeDateList.forEach((dateText) => {
          const dateInfo = roomItem[`${id}${dateText}`];
          // 这一天有房价信息 且用券了 且没过夜审
          if (dateInfo && dateInfo.couponNo && dateInfo.editFlag === 1) {
            // 间夜有使用券
            updateCouponInfoList.push({
              roomPrice: updatePrice,
              lockCouponNo: dateInfo.couponNo,
              businessDate: renderTableTitle(dateText),
            });
            updateCouponNoMap[dateInfo.couponNo] = true;
          }
        });
      });
      const updateCouponValueMap = await batchCalcCouponValue(
        { wyuid: orderBaseInfo.ownerWyuid, occType: OccType.DayUse },
        updateCouponInfoList,
      );
      // 更新押金
      const updateDepositList = dayPriceDataSource.map((roomItem) => {
        const { id, status } = roomItem;
        const updateDeposit = updateDepositValueMap[id];
        // 没有修改房间押金 或者 不是入住状态
        if (!updateDeposit || status !== SubOrderStatus.CheckIn) return roomItem;
        return { ...roomItem, deposit: new BigNumber(updateDeposit).toNumber() };
      });
      // 更新房价和券
      const updateRoomList = updateDepositList.map((roomItem) => {
        const { id, status } = roomItem;
        const updatePrice = updatePriceValueMap[id];
        // 没有修改房间房价 或者 已取消 或者 已退房订单
        if (
          !updatePrice ||
          status === SubOrderStatus.CancelReservation ||
          status === SubOrderStatus.CheckOut
        )
          return roomItem;
        const updateRoomInfo = {};
        const emptyCouponData = {
          couponAmount: null,
          couponName: null,
          couponNo: null,
          couponSubType: null,
          couponType: null,
          couponWyuid: null,
        }; // 空券信息
        const priceValue = new BigNumber(updatePrice).toNumber();
        dayRangeDateList.forEach((dateText) => {
          const dayItem = roomItem[`${id}${dateText}`];
          // 这一天有房价信息  且没过夜审
          if (dayItem && dayItem.editFlag === 1) {
            // 更新房价
            updateRoomInfo[dateText] = priceValue;
            let updateCouponInfo = {};
            // 当前间夜券金额重新计算过
            if (updateCouponNoMap[dayItem.couponNo]) {
              // 券的新面额
              const couponAmount = updateCouponValueMap[dayItem.couponNo];
              // 计算金额不为空 更新面值 否则清空优惠券信息
              updateCouponInfo = couponAmount
                ? { couponAmount: new BigNumber(couponAmount).dividedBy(100).toNumber() }
                : emptyCouponData;
            }
            // 更新券
            updateRoomInfo[`${id}${dateText}`] = {
              ...dayItem,
              price: priceValue,
              ...updateCouponInfo,
            };
          }
        });
        return { ...roomItem, ...updateRoomInfo };
      });
      const dayPriceMap = getAllPriceMap(OccType.DayUse, updateRoomList, dayRangeDateList);
      const hourPriceMap = getAllPriceMap(OccType.TimeRENT, hourPriceDataSource, hourRangeDateList);
      compareIsEqual({ ...dayPriceMap, ...hourPriceMap });
      saveDataFn({ dayPriceDataSource: updateRoomList });
    }
  };

  const updateDataSourceByTargetList = (occType, roomList) => {
    const list = occType === OccType.DayUse ? [...dayPriceDataSource] : [...hourPriceDataSource];
    roomList?.forEach((item) => {
      let roomDetail = list[item.virtualRoomNo];
      item?.datePrices?.forEach((subItem) => {
        const {
          key: id,
          couponAmount,
          couponName,
          couponNo,
          couponSubType,
          couponType,
          couponWyuid,
        } = subItem || {};
        Object.entries(roomDetail)
          ?.filter(([_, value]: any) => String(value?.id) === String(id))
          ?.forEach(([dateKey, dateValue]: any) => {
            roomDetail = {
              ...roomDetail,
              [dateKey]: {
                ...dateValue,
                couponAmount: Number(parseFen2Yuan(couponAmount)),
                couponName,
                couponNo,
                couponSubType,
                couponType,
                couponWyuid,
              },
            };
          });
      });
      list[item.virtualRoomNo] = roomDetail;
    });

    // 本地比较
    if (occType === OccType.DayUse) {
      const dayPriceMap = getAllPriceMap(OccType.DayUse, list, dayRangeDateList);
      const hourPriceMap = getAllPriceMap(OccType.TimeRENT, hourPriceDataSource, hourRangeDateList);
      compareIsEqual({ ...dayPriceMap, ...hourPriceMap });
      saveDataFn({ dayPriceDataSource: list });
    } else {
      const dayPriceMap = getAllPriceMap(OccType.DayUse, dayPriceDataSource, dayRangeDateList);
      const hourPriceMap = getAllPriceMap(OccType.TimeRENT, list, hourRangeDateList);
      compareIsEqual({ ...dayPriceMap, ...hourPriceMap });
      saveDataFn({ hourPriceDataSource: list });
    }
    // console.log('updateSource', list);
  };

  // 一键选券房间参数
  const formatRoomTypeList = (occType, source) => {
    return source?.map((item) => ({
      recNo: item.recNo,
      roomId: item.roomId,
      roomNo: item.roomNo,
      roomTypeId: item.roomTypeId,
      roomTypeName: item.roomTypeName,
      datePrices: Object.entries(item)
        ?.filter(([key]) => key.startsWith('['))
        ?.map(([key, value]) => {
          const datePriceItem = item[item.recNo + key];
          return {
            // 暂存券号，用于更新券状态
            couponNo: datePriceItem?.couponNo || undefined,
            couponAmount: parse2Number(parseYuan2Fen(datePriceItem?.couponAmount || 0)),
            couponName: datePriceItem?.couponName || undefined,
            couponSubType: datePriceItem?.couponSubType || undefined,
            couponType: datePriceItem?.couponType || undefined,
            couponWyuid: datePriceItem?.couponWyuid || undefined,
            key: `${datePriceItem?.id}`,
            id: datePriceItem?.id,
            occType,
            rentDate: renderTableTitle(key),
            roomPrice: parse2Number(parseYuan2Fen(value || 0)),
          };
        }),
    }));
  };

  // 批量选券
  const handleOpenBatchChooseModal = (occType) => {
    let dateStrRange = [];
    let priceRooms = [];
    if (occType === OccType.DayUse) {
      dateStrRange =
        dayRangeDateList?.length > 0
          ? [
              renderTableTitle(dayRangeDateList[0]),
              renderTableTitle(dayRangeDateList[dayRangeDateList?.length - 1]),
            ]
          : null;
      priceRooms = formatRoomTypeList(occType, dayPriceDataSource);
    } else {
      dateStrRange =
        hourRangeDateList?.length > 0
          ? [
              renderTableTitle(hourRangeDateList[0]),
              renderTableTitle(hourRangeDateList[hourRangeDateList?.length - 1]),
            ]
          : null;
      priceRooms = formatRoomTypeList(occType, hourPriceDataSource);
    }
    batchChooseCouponRef.current.openModal({
      extra: {
        wyuid: orderBaseInfo.ownerWyuid,
        resvNo: orderBaseInfo.resvNo,
      },
      dateStrRange,
      priceRooms,
      onConfirm({ priceRooms: roomList }) {
        // console.log('onConfirm', roomList);
        updateDataSourceByTargetList(occType, roomList);

        // 更新券选择锁定状态（临时状态）
        const couponStatusMap = {};
        roomList.forEach((item) => {
          item.datePrices.forEach((subItem) => {
            if (subItem.couponNo) {
              couponStatusMap[subItem.couponNo] = CouponStatus.Locked;
            }
          });
        });
        updateBatchWyCouponStatus(couponStatusMap);
        // 更新已选缓存
        selectCouponRef.current.batchUpdateCacheCouponStatus(couponStatusMap);
      },
      onReset({ priceRooms: roomList }) {
        updateDataSourceByTargetList(occType, roomList);

        const couponStatusMap = {};
        roomList.forEach((item) => {
          item.datePrices.forEach((subItem) => {
            if (subItem.couponNo) {
              couponStatusMap[subItem.couponNo] = CouponStatus.Effective;
            }
          });
        });
        updateBatchWyCouponStatus(couponStatusMap);
        selectCouponRef.current.batchUpdateCacheCouponStatus(couponStatusMap);
      },
    });
  };

  return (
    <>
      <SelectDiscountCoupon ref={selectCouponRef} />
      <BatchCouponModal ref={batchChooseCouponRef} />

      {/* 房价信息 */}
      <div className={style.container} style={{ marginTop: '40px' }}>
        <div className="groupLabel">{renderIntlLabel('order_title_roomPrice_info')}</div>
        {dayPriceDataSource.length > 0 && (
          <>
            <div className={style.groupAction}>
              <span>{renderIntlLabel('order_title_roomPrice_of_dayroom')}</span>
              <span>
                {renderIntlLabel('order_title_total_room_price')}：
                {parseYuan2Currency(totalDayPrice)}
              </span>
              <div style={{ flex: 1, textAlign: 'right' }}>
                <Button onClick={showBatchPriceModal}>
                  {renderIntlLabel('order_title_batch_room_price')}
                </Button>
                <Button
                  style={{ marginLeft: 8 }}
                  disabled={!isShowChooseBtn}
                  onClick={() => handleOpenBatchChooseModal(OccType.DayUse)}
                >
                  {renderIntlLabel('order_title_One_click_coupon_selection')}
                </Button>
              </div>
            </div>
            <Skeleton loading={orderRoomInfoLoading}>
              <EditCellTable
                columns={dayRoomColumns}
                dataSource={dayPriceDataSource}
                onSaveCellChange={handleSaveDayCellChange}
                pagination={false}
                summary={(pageData) => {
                  const datePriceCountsArray = tableSummary(pageData, dayRangeDateList);
                  return (
                    <>
                      <Table.Summary.Row key={0}>
                        <Table.Summary.Cell key={0} index={0}>
                          {renderIntlLabel('order_goods_subtotal')}
                        </Table.Summary.Cell>
                        {datePriceCountsArray.map((count, index) => (
                          <Table.Summary.Cell
                            className={index > 0 ? style.priceDisable : ''}
                            key={uuid()}
                            index={index + 1}
                            align={index > 0 ? 'right' : 'left'}
                          >
                            {index > 0 ? (
                              <span style={{ marginRight: '4px' }}>{count}</span>
                            ) : (
                              count
                            )}
                          </Table.Summary.Cell>
                        ))}
                      </Table.Summary.Row>
                    </>
                  );
                }}
                scroll={{ x: 'max-content', y: 840 }}
              />
            </Skeleton>
          </>
        )}
        {hourPriceDataSource.length > 0 && (
          <>
            <div className={style.groupAction}>
              <span>{renderIntlLabel('order_title_roomPrice_of_hourroom')}</span>
              <span>
                {renderIntlLabel('order_title_total_room_price')}：
                {parseYuan2Currency(totalHourPrice)}
              </span>
              <div style={{ flex: 1, textAlign: 'right' }}>
                <Button
                  disabled={!isShowChooseBtn}
                  onClick={() => handleOpenBatchChooseModal(OccType.TimeRENT)}
                >
                  {renderIntlLabel('order_title_One_click_coupon_selection')}
                </Button>
              </div>
            </div>
            <Skeleton loading={orderRoomInfoLoading}>
              <EditCellTable
                columns={hourRoomColumns}
                dataSource={hourPriceDataSource}
                onSaveCellChange={handleSaveHourCellChange}
                pagination={false}
                summary={(pageData) => {
                  const datePriceCountsArray = tableSummary(pageData, hourRangeDateList);
                  return (
                    <>
                      <Table.Summary.Row key={1}>
                        <Table.Summary.Cell key={0} index={0}>
                          {renderIntlLabel('order_goods_subtotal')}
                        </Table.Summary.Cell>
                        {datePriceCountsArray.map((count, index) => (
                          <Table.Summary.Cell
                            className={index > 0 ? style.priceDisable : ''}
                            key={uuid()}
                            index={index + 1}
                            align={index > 0 ? 'right' : 'left'}
                          >
                            {index > 0 ? (
                              <span style={{ marginRight: '4px' }}>{count}</span>
                            ) : (
                              count
                            )}
                          </Table.Summary.Cell>
                        ))}
                      </Table.Summary.Row>
                    </>
                  );
                }}
                scroll={{ x: 'max-content', y: 840 }}
              />
            </Skeleton>
          </>
        )}

        <AuthBtn>
          <Button
            data-authkey={PERM_CODES.SAVE_PRICE}
            type="primary"
            style={{ marginTop: '24px' }}
            onClick={handleSaveAllPrice}
            disabled={!isModify}
          >
            {renderIntlLabel('order_title_save_roomPrice')}
          </Button>
        </AuthBtn>
      </div>
      <BatchModifyPriceModal
        tipNode={
          <div style={{ margin: '-12px 0 12px', fontSize: 14 }}>
            <p>{renderIntlLabel('order_title_roomPrice_adjust1')}</p>
            <p>{renderIntlLabel('order_title_roomPrice_adjust2')}</p>
          </div>
        }
        innerRef={batchModalRef}
        onEnd={handleBatchUpdatePrice}
      />
    </>
  );
};

const mapStateToProps = (state) => {
  const { wangyuCouponList, manualCouponList } = state.guest;
  return {
    ...state.order,
    wangyuCouponList,
    manualCouponList,
  };
};

const ConnectComponent: React.FC<any> = connect(mapStateToProps, {
  saveDataFn: saveData,
  updateOrderRoomPriceFn: updateOrderRoomPrice,
  getWangyuCouponListFn: getWangyuCouponList,
  getManualCouponListFn: getManualCouponList,
})(React.memo(RoomPrice));

export default React.forwardRef<any, any>((props, ref) => (
  <ConnectComponent {...props} myRef={ref} />
));
